/**
 * ADR-007 runtime-config accessor for GMS / SmartStudio.
 *
 * Endpoint URLs are injected at RUNTIME, not baked into the build:
 * `public/runtime-config.js` sets `window.__GMS_RUNTIME_CONFIG__` via a plain
 * <script> in index.html BEFORE the app module bundle loads. This lets a single
 * built bundle run unchanged across dev/qa/stage/prod — deploy overwrites
 * runtime-config.js per environment (deploy moves a build; release flips a flag).
 *
 * The committed public/runtime-config.js holds the local-dev defaults so
 * `npm run dev` works out of the box, matching the old .env behaviour.
 * See MIGRATION-NOTES.md for the full contract and where deploy writes the file.
 */

/** Shape of window.__GMS_RUNTIME_CONFIG__ (all keys optional). */
export interface GmsRuntimeConfig {
  SAMS_API_URL?: string;
  SAMS_SSO_URL?: string;
  GMS_BACKEND_API_URL?: string;
  ARS_PROMOTION_API_URL?: string;
  // Retired 2026-07-30 — DB Promoter card and admin Task Queue screen.
  // DBPROMOTER_APP_URL?: string;
  // TASKQUEUE_WORKER_INFO_URL?: string;
  CONNECTION_API?: string;
  ROOT_PREVIEW_URL?: string;
  ISR_BACKEND_URL?: string;
  ISR_RUNTIME_URL?: string;
}

declare global {
  interface Window {
    __GMS_RUNTIME_CONFIG__?: GmsRuntimeConfig;
  }
}

/** Raw runtime config, `{}` when absent (tests / misconfigured deploy). */
export function getRuntimeConfig(): GmsRuntimeConfig {
  return (typeof window !== 'undefined' && window.__GMS_RUNTIME_CONFIG__) || {};
}

const raw = getRuntimeConfig();

/**
 * Resolved config used across the app. Endpoint values come from
 * window.__GMS_RUNTIME_CONFIG__; the ISR_* keys keep the historical fallbacks
 * that used to be hardcoded in AppSettings so behaviour is unchanged when a
 * deploy omits them.
 */
export const runtimeConfig = {
  SAMS_API_URL: raw.SAMS_API_URL ?? '',
  SAMS_SSO_URL: raw.SAMS_SSO_URL ?? '',
  GMS_BACKEND_API_URL: raw.GMS_BACKEND_API_URL ?? '',
  ARS_PROMOTION_API_URL: raw.ARS_PROMOTION_API_URL ?? '',
  // Retired 2026-07-30 — DB Promoter card and admin Task Queue screen.
  // DBPROMOTER_APP_URL: raw.DBPROMOTER_APP_URL ?? '',
  // TASKQUEUE_WORKER_INFO_URL: raw.TASKQUEUE_WORKER_INFO_URL ?? '',
  CONNECTION_API: raw.CONNECTION_API ?? '',
  ROOT_PREVIEW_URL: raw.ROOT_PREVIEW_URL ?? '',
  ISR_BACKEND_URL: raw.ISR_BACKEND_URL ?? 'https://isr-api-sbp-test.compatio.net/',
  ISR_RUNTIME_URL: raw.ISR_RUNTIME_URL ?? 'https://sb3-test-sbp.compatio.net/',
};

export default runtimeConfig;
